<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import {
  checkFileSizeLimit,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { fileNameOf, fileExtOf } from '../../../composables/useCsmlNodeMeta';

const props = defineProps({
  modelValue: { type: String, default: '' },
  kind: {
    type: String,
    default: 'file',
    validator: value => ['file', 'video', 'audio'].includes(value),
  },
  // The node that was selected when this uploader was shown. Captured before
  // the request so a later selection cannot receive the finished URL.
  nodeId: { type: [Number, String], default: null },
});

const emit = defineEmits(['update:modelValue', 'uploaded']);

// WhatsApp-deliverable formats per node kind.
const EXTENSIONS = {
  file: [
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'csv',
    'txt',
    'zip',
  ],
  video: ['mp4', '3gp'],
  audio: ['mp3', 'ogg', 'aac', 'm4a', 'amr'],
};

const { t } = useI18n();
const globalConfig = useMapGetter('globalConfig/get');

const fileInput = ref(null);
const isUploading = ref(false);
const errorKey = ref('');

const kindKey = computed(() => props.kind.toUpperCase());
const extensions = computed(() => EXTENSIONS[props.kind]);
const accept = computed(() => extensions.value.map(ext => `.${ext}`).join(','));
const maxSizeMb = computed(() =>
  resolveMaximumFileUploadSize(globalConfig.value?.maximumFileUploadSize)
);
const fileName = computed(() => fileNameOf(props.modelValue));
const fileExt = computed(() => fileExtOf(props.modelValue));
const hasFile = computed(() => Boolean(props.modelValue));
const errorMessage = computed(() => {
  if (!errorKey.value) return '';
  return t(`AGENT_BOTS.VISUAL_BUILDER.FILE_UPLOAD.${errorKey.value}`, {
    size: maxSizeMb.value,
    formats: extensions.value.join(', ').toUpperCase(),
  });
});

const isAccepted = file => {
  const ext = (file.name || '').split('.').pop().toLowerCase();
  return extensions.value.includes(ext);
};

const openPicker = () => {
  if (isUploading.value) return;
  fileInput.value?.click();
};

const onFileChange = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  errorKey.value = '';

  if (!isAccepted(file)) {
    errorKey.value = 'TYPE_ERROR';
    return;
  }
  if (!checkFileSizeLimit(file, maxSizeMb.value)) {
    errorKey.value = 'SIZE_ERROR';
    return;
  }

  const targetNodeId = props.nodeId;
  isUploading.value = true;
  try {
    const { fileUrl } = await uploadFile(file);
    if (!fileUrl) {
      errorKey.value = 'ERROR';
      return;
    }
    emit('uploaded', { nodeId: targetNodeId, url: fileUrl });
  } catch {
    errorKey.value = 'ERROR';
  } finally {
    isUploading.value = false;
  }
};

const removeFile = () => {
  errorKey.value = '';
  emit('update:modelValue', '');
};

const onUrlChange = value => {
  errorKey.value = '';
  emit('update:modelValue', value);
};
</script>

<template>
  <div class="flex flex-col min-w-0 gap-1">
    <span class="mb-0.5 text-heading-3 text-n-slate-12">
      {{ t(`AGENT_BOTS.VISUAL_BUILDER.FILE_UPLOAD.${kindKey}.LABEL`) }}
    </span>
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      :accept="accept"
      @change="onFileChange"
    />

    <div
      v-if="hasFile"
      class="flex flex-col gap-2 p-2 border rounded-lg border-n-weak bg-n-alpha-1"
    >
      <div class="flex items-center gap-2 min-w-0">
        <Icon
          icon="i-lucide-file-text"
          class="size-5 shrink-0 text-n-slate-11"
        />
        <span
          class="flex-1 min-w-0 text-sm truncate text-n-slate-12"
          :title="modelValue"
        >
          {{ fileName || modelValue }}
        </span>
        <span
          v-if="fileExt"
          class="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-n-alpha-2 text-n-slate-11"
        >
          {{ fileExt }}
        </span>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <Button
          size="xs"
          variant="faded"
          color="slate"
          icon="i-lucide-upload"
          :label="t('AGENT_BOTS.VISUAL_BUILDER.FILE_UPLOAD.REPLACE')"
          :is-loading="isUploading"
          :disabled="isUploading"
          @click="openPicker"
        />
        <a
          :href="modelValue"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 text-xs text-n-slate-11 hover:text-n-slate-12"
        >
          <Icon icon="i-lucide-external-link" class="size-3" />
          {{ t('AGENT_BOTS.VISUAL_BUILDER.FILE_UPLOAD.OPEN') }}
        </a>
        <Button
          size="xs"
          variant="ghost"
          color="ruby"
          icon="i-lucide-trash-2"
          :label="t('AGENT_BOTS.VISUAL_BUILDER.REMOVE')"
          :disabled="isUploading"
          @click="removeFile"
        />
      </div>
    </div>

    <button
      v-else
      type="button"
      class="flex flex-col items-center justify-center gap-1.5 px-3 py-6 text-center border border-dashed rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-60"
      :disabled="isUploading"
      :aria-describedby="errorMessage ? 'csml-file-error' : undefined"
      @click="openPicker"
    >
      <Icon icon="i-lucide-file-up" class="size-4" />
      <span class="text-xs">
        {{
          isUploading
            ? t('AGENT_BOTS.VISUAL_BUILDER.FILE_UPLOAD.UPLOADING')
            : t(`AGENT_BOTS.VISUAL_BUILDER.FILE_UPLOAD.${kindKey}.PROMPT`)
        }}
      </span>
      <span class="text-[10px] text-n-slate-10">
        {{
          t('AGENT_BOTS.VISUAL_BUILDER.FILE_UPLOAD.HINT', {
            formats: extensions.join(', ').toUpperCase(),
            size: maxSizeMb,
          })
        }}
      </span>
    </button>

    <p
      v-if="errorMessage"
      id="csml-file-error"
      role="alert"
      class="m-0 text-xs text-n-ruby-11"
    >
      {{ errorMessage }}
    </p>

    <p class="mt-2 mb-0 text-xs text-center text-n-slate-10">
      {{ t('AGENT_BOTS.VISUAL_BUILDER.FILE_UPLOAD.OR') }}
    </p>
    <Input
      :model-value="modelValue"
      size="sm"
      :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.URL')"
      :placeholder="t('AGENT_BOTS.VISUAL_BUILDER.FILE_UPLOAD.URL_PLACEHOLDER')"
      :disabled="isUploading"
      @update:model-value="onUrlChange"
    />
    <p class="m-0 text-[11px] text-n-slate-10">
      {{ t(`AGENT_BOTS.VISUAL_BUILDER.FILE_UPLOAD.${kindKey}.NOTE`) }}
    </p>
  </div>
</template>
